import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { Loader, BookOpen, CircleDollarSign } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  cover_image: string;
  created_at: string;
}

function BlogList() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setPosts(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-md">
        {error}
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Numismatic Blog - OldCoinage.com</title>
        <meta name="description" content="Read our latest articles about coin collecting, numismatics, and rare currency notes." />
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Numismatic Insights</h1>
          <p className="text-xl text-gray-600">
            Expert articles on coin collecting, authentication, and market trends
          </p>
        </div>
        
        <div className="space-y-12">
          {posts.map((post) => (
            <article key={post.id} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 transition-all hover:shadow-xl">
              <Link to={`/blog/${post.slug}`} className="block">
                <div className="aspect-video bg-gray-100 relative">
                  <img
                    src={post.cover_image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.src = '/old_coinage_logo.jpeg';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent opacity-0 hover:opacity-100 transition-opacity" />
                </div>
              </Link>
              <div className="p-6">
                <time className="text-sm text-gray-500">
                  {format(new Date(post.created_at), 'MMMM d, yyyy')}
                </time>
                <Link to={`/blog/${post.slug}`}>
                  <h2 className="mt-2 text-2xl font-bold text-gray-900 hover:text-primary-600 transition-colors">
                    {post.title}
                  </h2>
                </Link>
                <p className="mt-3 text-gray-600 line-clamp-3">{post.excerpt}</p>
                <Link
                  to={`/blog/${post.slug}`}
                  className="mt-4 inline-flex items-center text-primary-600 hover:text-primary-800 font-medium"
                >
                  <span>Read more</span>
                  <BookOpen className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </>
  );
}

export default BlogList;